import { useLayoutEffect, useEffectEvent, useState } from "react";
import { useTranslations } from "use-intl";
import { Button, Typography } from "@mui/material";
import { useRouter } from "@tanstack/react-router";
import * as Sentry from "@sentry/react";
import { showSnackbar } from "@/app/lib/context/SnackbarContext";
import type { SignInError } from "@/app/lib/errors/SignInError";
import { isWeb } from "@/app/lib/platform";
import { isProduction } from "@/app/lib/constants";
import { Image } from "../Image";
import { LogInForm } from "../web/LogInForm";

const TryAgainButton = () => {
  const t = useTranslations();

  const [isNavigating, setIsNavigating] = useState(false);
  const { invalidate } = useRouter();

  return (
    <Button
      onClick={async () => {
        setIsNavigating(true);
        try {
          await invalidate();
        } catch (error) {
          Sentry.captureException(error);
        } finally {
          setIsNavigating(false);
        }
      }}
      loading={isNavigating}
      variant="contained"
      color="primary"
    >
      {t("welcome.button")}
    </Button>
  );
};

type Props = {
  error: SignInError;
};

export const SignInErrorMessage = ({ error }: Props) => {
  const t = useTranslations();

  const showError = useEffectEvent(() => {
    showSnackbar({
      content:
        error.cause === "cancelled"
          ? t("welcome.loginCancelled")
          : t("welcome.errorMessage"),
      type: "error",
    });
  });

  useLayoutEffect(() => {
    const timeout = setTimeout(() => {
      showError();
    }, 200);

    return () => clearTimeout(timeout);
  }, []);

  return (
    <div className="flex h-screen w-screen flex-col items-center justify-center p-4 text-center">
      <div className="-mt-20 mb-20 h-18 w-40">
        <Image src="/rahallogo.svg" aria-hidden />
      </div>
      <Typography variant="h4">{t("welcome.title")}</Typography>
      <Typography variant="body1" sx={{ mb: 3 }}>
        {t("welcome.body")}
      </Typography>

      {isWeb && !isProduction ? <LogInForm /> : <TryAgainButton />}
    </div>
  );
};
